import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import TextArea from "../components/ui/TextArea";
import Button from "../components/ui/Button";
import {
  submitPurchase,
  fetchEstates,
  fetchAvailableProperties,
  fetchHouses,
  fetchPublicPlots,
  fetchPropertyById,
  fetchPlotById,
} from "../services/api";
import DateInput from "../components/ui/DateInput";
import "../styles/Purchase.css";
import Swal from "sweetalert2";
import { PaystackButton } from "react-paystack";
import TermsAndConditions from "../components/TermsAndConditions";
import Chat from "../components/Chat";
import Loader2 from "../components/Loader2";

const Purchase = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [allProperties, setAllProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const { id } = useParams();

  const [formData, setFormData] = useState({
    title: "",
    name: "",
    emailAddress: "",
    phoneNumber: "",
    dob: "",
    maritalStatus: "",
    gender: "",
    stateOfOrigin: "",
    lga: "",
    nationality: "",
    state: "",
    city: "",
    residentialAddress: "",
    countryResidence: "",
    employmentStatus: "",
    employerAddress: "",
    employerCity: "",
    employerCountry: "",
    employer: "",
    motherMaidenName: "",
    nextOfKinName: "",
    nextOfKinRelationship: "",
    nextOfKinCity: "",
    nextOfKinResidentialAddress: "",
    nextOfKinTelephone: "",
    paymentMethod: "",
    propertyId: "",
    propertyType: "",
    additionalInformation: "",
    documents: [],
    paymentReceipt: "",
    agentId: "",
  });
  const [isLoading, setLoading] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const config = {
    reference: new Date().getTime().toString(),
    email: formData.emailAddress,
    amount: 2000000, // Amount in kobo (20,000 Naira)
    publicKey: "pk_test_3d04b947798237ffa272e36fc9d1f891ff6e5480",
  };

  const relationshipOptions = [
    { value: "Brother/Sister", label: "Brother/Sister" },
    { value: "Mother/Father", label: "Mother/Father" },
    { value: "Uncle/Aunt", label: "Uncle/Aunt" },
    { value: "Son/Daughter", label: "Son/Daughter" },
    { value: "Spouse", label: "Spouse" },
  ];

  const paymentOptions = [
    { value: "Full Payment", label: "Full Payment" },
    { value: "Part Payment", label: "Part payment" },
  ];

  const getPropertyDetails = async (id) => {
    try {
      setLoading(true);
      const response =
        type == "house" ? await fetchPropertyById(id) : await fetchPlotById(id); // Get first 100 plots
      if (response.success) {
        const property = response.payload;
        setSelectedProperty(response.payload);
        setFormData({
          ...formData,
          propertyId: property.id,
          propertyType: type.toUpperCase(),
        });
      }
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };
  const fetchPropertiesByType = async (type) => {
    try {
      setLoading(true);
      let response;
      if (type === "PLOT") {
        response = await fetchPublicPlots(0, 20); // Get first 100 plots
      } else if (type === "HOUSE") {
        response = await fetchHouses(0, 20); // Get first 100 houses
      } else {
        throw new Error(`Unknown property type: ${type}`);
      }
      // Handle different response structures
      let propertiesList = [];
      if (Array.isArray(response)) {
        propertiesList = response;
      } else if (response?.payload) {
        propertiesList =
          response.payload.content ||
          response.payload.page ||
          response.payload ||
          [];
      } else if (response?.data) {
        propertiesList =
          response.data.payload?.content ||
          response.data.payload?.page ||
          response.data ||
          [];
      }

      if (!Array.isArray(propertiesList)) {
        console.error("Invalid properties data format:", propertiesList);
        throw new Error("Invalid properties data format received from server");
      }
      return propertiesList;
    } catch (error) {
      console.error(`Error fetching ${type} properties:`, {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      let errorMessage = `Failed to load ${type.toLowerCase()} properties. `;
      errorMessage +=
        error.response?.data?.message ||
        error.message ||
        "Please try again later.";

      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });

      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle property type change
  const handlePropertyTypeChange = async (e) => {
    const { value } = e.target;

    // Update the form data
    setFormData((prev) => ({
      ...prev,
      propertyType: value,
      propertyId: "", // Reset propertyId when type changes
    }));

    // Only fetch properties if a type is selected
    if (value) {
      const properties = await fetchPropertiesByType(value);
      setFilteredProperties(properties);
    } else {
      setFilteredProperties([]);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    const phoneRegex = /^[0-9]{11}$/; // Validates 11-digit phone numbers
    return phoneRegex.test(phone.replace(/[^0-9]/g, ""));
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      setFormData((prev) => ({
        ...prev,
        isValidPhone: isValidPhone(value),
        [name]: value,
      }));
    } else if (name === "emailAddress") {
      setFormData((prev) => ({
        ...prev,
        isValidEmail: isValidEmail(value),
        [name]: value,
      }));
    } else if (name === "propertyId") {
      setFormData((prev) => ({
        ...prev,
        [name]: Number(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        const step1Valid =
          formData.title &&
          formData.name &&
          formData.emailAddress &&
          formData.phoneNumber &&
          formData.dob &&
          formData.maritalStatus &&
          formData.gender &&
          formData.stateOfOrigin &&
          formData.lga &&
          formData.nationality;
        console.log("Step 1 validation:", { step1Valid, formData });
        return step1Valid;

      case 2:
        const step2Valid = formData.employmentStatus;
        console.log("Step 2 validation:", { step2Valid, formData });
        return step2Valid;

      case 3:
        const step3Valid =
          formData.nextOfKinName &&
          formData.nextOfKinRelationship &&
          formData.nextOfKinResidentialAddress &&
          formData.nextOfKinCity;
        console.log("Step 3 validation:", { step3Valid, formData });
        return step3Valid;

      case 4:
        const step4Valid =
          formData.paymentMethod &&
          typeof formData.propertyId === "number" &&
          !isNaN(formData.propertyId) &&
          formData.propertyId > 0;
        console.log("Step 4 validation:", { step4Valid, formData });
        return step4Valid;

      case 5:
        return true; // Allow step 5 to be displayed
      default:
        return false;
    }
  };

  const nextStep = () => {
    console.log("Current step:", step);

    if (step < 5) {
      if (isStepValid()) {
        setStep(step + 1);
        console.log("Moving to step:", step + 1);
      } else {
        Swal.fire({
          title: "Required Fields",
          text: "Please fill in all required fields before proceeding.",
          icon: "warning",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      console.log("Moving back to step:", step - 1);
    }
  };

  const handlePaystackSuccess = async (reference) => {
    try {
      setLoading(true);
      const finalFormData = {
        ...formData,
        paymentReference: reference.reference,
        applicationFee: 20000,
        agentId: formData.agentId || null, // Ensure agentId is null if empty
      };

      const response = await submitPurchase(finalFormData);
      if (response.success) {
        setFormData({
          title: "",
          name: "",
          emailAddress: "",
          phoneNumber: "",
          dob: "",
          maritalStatus: "",
          gender: "",
          stateOfOrigin: "",
          lga: "",
          nationality: "",
          state: "",
          city: "",
          residentialAddress: "",
          countryResidence: "",
          employmentStatus: "",
          employer: "",
          employerAddress: "",
          employerCity: "",
          employerCountry: "",
          motherMaidenName: "",
          nextOfKinName: "",
          nextOfKinRelationship: "",
          nextOfKinCity: "",
          nextOfKinResidentialAddress: "",
          nextOfKinTelephone: "",
          paymentMethod: "",
          propertyId: "",
          additionalInformation: "",
        });
        Swal.fire({
          title: "Success!",
          text: "Your application has been submitted successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/");
        });
      }
    } catch (error) {
      console.error("Error submitting purchase:", error);
      Swal.fire({
        title: "Error!",
        text: "There was an error submitting your application",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaystackClose = () => {
    Swal.fire({
      title: "Payment Cancelled",
      text: "You have cancelled the payment process",
      icon: "info",
      confirmButtonText: "OK",
    });
  };

  const handleDownloadForm = () => {
    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = "/forms.pdf";
    link.download = "forms.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Initialize with empty properties
  useEffect(() => {
    if (id) {
      getPropertyDetails(id);
    } else {
      setAllProperties([]);
      setFilteredProperties([]);
    }
  }, [id]);

  return (
    <>
      {/* <Header /> */}
      <Chat />
      <div className="purchase-form-wrapper space">
        <div className="container">
          <div className="col-xl-12">
            <div className="title-area d-flex justify-content-between align-items-center">
              <div>
                <h2 className="sec-title">Purchase Form</h2>
                <p>Please fill in your details to proceed with the purchase</p>
              </div>
              <Button onClick={handleDownloadForm} className="download-btn">
                <i className="fas fa-download me-2"></i>
                Download Form
              </Button>
            </div>

            <div className="form-steps-wrapper">
              <div className="form-steps-nav">
                <div className={`step-indicator ${step >= 1 ? "active" : ""}`}>
                  1
                </div>
                <div className={`step-indicator ${step >= 2 ? "active" : ""}`}>
                  2
                </div>
                <div className={`step-indicator ${step >= 3 ? "active" : ""}`}>
                  3
                </div>
                <div className={`step-indicator ${step >= 4 ? "active" : ""}`}>
                  4
                </div>
                <div className={`step-indicator ${step >= 5 ? "active" : ""}`}>
                  5
                </div>
              </div>

              <div className="form-step-content">
                {step === 1 && (
                  <div className="row">
                    <div className="col-12">
                      <h3 className="form-section-title">
                        Personal Information
                      </h3>
                    </div>
                    <div className="col-md-6">
                      <Select
                        label="Title"
                        name="title"
                        options={[
                          { value: "Mr.", label: "Mr." },
                          { value: "Mrs.", label: "Mrs." },
                          { value: "Ms.", label: "Ms." },
                        ]}
                        value={formData.title}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Email Address"
                        name="emailAddress"
                        type="email"
                        value={formData.emailAddress}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Phone Number"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <DateInput
                        label="Date of Birth"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        required
                        max={new Date().toISOString().split("T")[0]}
                        min="1900-01-01"
                      />
                    </div>
                    <div className="col-md-6">
                      <Select
                        label="Marital Status"
                        name="maritalStatus"
                        options={[
                          { value: "Single", label: "Single" },
                          { value: "Married", label: "Married" },
                          { value: "Divorced", label: "Divorced" },
                          { value: "Widowed", label: "Widowed" },
                        ]}
                        value={formData.maritalStatus}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Select
                        label="Gender"
                        name="gender"
                        options={[
                          { value: "Male", label: "Male" },
                          { value: "Female", label: "Female" },
                          { value: "Other", label: "Other" },
                        ]}
                        value={formData.gender}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Nationality"
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="State"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <TextArea
                        label="Residential Address"
                        name="residentialAddress"
                        value={formData.residentialAddress}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Country of Residence"
                        name="countryResidence"
                        value={formData.countryResidence}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="State of Origin"
                        name="stateOfOrigin"
                        value={formData.stateOfOrigin}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Local Government Area"
                        name="lga"
                        value={formData.lga}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Mother's Maiden Name"
                        name="motherMaidenName"
                        value={formData.motherMaidenName}
                        onChange={handleChange}
                      />
                    </div>
                    {/* <div className="col-md-6">
                      <Input
                        label="Agent ID (if any)"
                        name="agentId"
                        type="number"
                        value={formData.agentId}
                        onChange={handleChange}
                      />
                    </div> */}
                  </div>
                )}

                {step === 2 && (
                  <div className="row">
                    <div className="col-12">
                      <h3 className="form-section-title">
                        Employment Information
                      </h3>
                    </div>
                    <div className="col-md-6">
                      <Select
                        label="Employment Status"
                        name="employmentStatus"
                        options={[
                          { value: "Employed", label: "Employed" },
                          {
                            value: "Self-Employed",
                            label: "Self-Employed",
                          },
                          { value: "Retired", label: "Retired" },
                          { value: "Unemployed", label: "Unemployed" },
                        ]}
                        value={formData.employmentStatus}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Employer Name"
                        name="employer"
                        value={formData.employer}
                        onChange={handleChange}
                        required={formData.employmentStatus === "Employed"}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Employer Address"
                        name="employerAddress"
                        value={formData.employerAddress}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Employer City"
                        name="employerCity"
                        value={formData.employerCity}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Employer Country"
                        name="employerCountry"
                        value={formData.employerCountry}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="row">
                    <div className="col-12">
                      <h3 className="form-section-title">
                        Next of Kin Information
                      </h3>
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Next of Kin Name"
                        name="nextOfKinName"
                        value={formData.nextOfKinName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Select
                        label="Relationship with Next of Kin"
                        name="nextOfKinRelationship"
                        options={relationshipOptions}
                        value={formData.nextOfKinRelationship}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Next of Kin City"
                        name="nextOfKinCity"
                        value={formData.nextOfKinCity}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        type="tel"
                        label="Next of Telephone"
                        name="nextOfKinTelephone"
                        value={formData.nextOfKinTelephone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <TextArea
                        label="Next of Kin Residential Address"
                        name="nextOfKinResidentialAddress"
                        value={formData.nextOfKinResidentialAddress}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="row">
                    <div className="col-12">
                      <h3 className="form-section-title">
                        Property and Payment Details
                      </h3>
                    </div>
                    {selectedProperty && selectedProperty.id ? (
                      <></>
                    ) : (
                      <>
                        <div className="col-md-6">
                          <Select
                            label="Property of choice"
                            name="propertyId"
                            value={formData.propertyId}
                            onChange={handleChange}
                            options={filteredProperties.map((property) => {
                              console.log("Property:", property);

                              // For plots
                              if (formData.propertyType === "PLOT") {
                                const plotNumber = property.plotNumber || "N/A";
                                const dimension = property.dimension || "";
                                const estate = property.estate?.name
                                  ? ` - ${property.estate.name}`
                                  : "";
                                return {
                                  value: property.id,
                                  label: `Plot ${plotNumber}${
                                    dimension ? ` (${dimension})` : ""
                                  }${estate}`.trim(),
                                };
                              }

                              // For houses
                              if (formData.propertyType === "HOUSE") {
                                const houseNumber =
                                  property.houseNumber || "N/A";
                                const bedrooms = property.bedrooms
                                  ? `, ${property.bedrooms} BD`
                                  : "";
                                const estate = property.estate?.name
                                  ? ` - ${property.estate.name}`
                                  : "";
                                return {
                                  value: property.id,
                                  label:
                                    `House ${houseNumber}${bedrooms}${estate}`.trim(),
                                };
                              }

                              // Fallback
                              return {
                                value: property.id,
                                label:
                                  property.name || `Property ${property.id}`,
                              };
                            })}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <Select
                            label="Property Type"
                            name="propertyType"
                            value={formData.propertyType}
                            onChange={handlePropertyTypeChange}
                            options={[
                              { value: "HOUSE", label: "House" },
                              { value: "PLOT", label: "Plot" },
                            ]}
                            required
                          />
                        </div>
                      </>
                    )}

                    <div className="col-md-6">
                      <Select
                        label="Payment Method"
                        name="paymentMethod"
                        options={paymentOptions}
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Upload Documents (PDF, JPG, PNG)
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setFormData((prev) => ({
                              ...prev,
                              documents: files,
                            }));
                          }}
                        />
                        <small className="text-muted">
                          You can upload multiple files
                        </small>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment Receipt (if available)
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setFormData((prev) => ({
                              ...prev,
                              paymentReceipt: file,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {step === 5 && (
                  <div className="terms-step">
                    <div className="terms-wrapper">
                      <TermsAndConditions />
                      <div className="form-check mt-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="acceptTerms"
                          checked={true}
                          onChange={(e) =>
                            setHasAcceptedTerms(e.target.checked)
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="acceptTerms"
                          checked={true}
                        >
                          I accept the terms and conditions
                        </label>
                      </div>
                      <div className="flex text-center mt-4">
                        <Button
                          type="button"
                          onClick={prevStep}
                          className="prev-btn me-2"
                        >
                          <i className="fa fa-arrow-left"></i> Previous
                        </Button>

                        {isLoading ? (
                          <Button
                            type="button"
                            className="prev-btn me-2"
                            disabled
                          >
                            <Loader2 /> Please Wait..
                          </Button>
                        ) : (
                          <PaystackButton
                            {...config}
                            text="Submit Application"
                            onSuccess={handlePaystackSuccess}
                            onClose={handlePaystackClose}
                            id="paystack-button"
                            className="paystack-button bg-secondary"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-navigation mt-4">
                  {step > 1 && step < 5 && (
                    <Button
                      type="button"
                      onClick={prevStep}
                      className="prev-btn me-2"
                    >
                      <i className="fa fa-arrow-left"></i> Previous
                    </Button>
                  )}
                  {step < 5 ? (
                    <Button
                      type="button"
                      onClick={nextStep}
                      className="next-btn"
                    >
                      Next <i className="fa fa-arrow-right"></i>
                    </Button>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <Footer /> */}
    </>
  );
};

export default Purchase;
